import { useId } from 'react';

/**
 * Groups a set of SelectableCards as a real fieldset with an accessible name.
 *
 * The original markup grouped radios visually with an <h3>/<h4> and no programmatic
 * relationship, so a screen reader entering the plan selector announced only "radio, 1 of 2"
 * with no indication of what was being chosen. <fieldset> + <legend> is the native fix.
 *
 * The legend is sr-only by default because the visible heading already renders above (via
 * SectionCard or a question title); duplicating it on screen would be redundant, but the
 * accessible name still needs to exist.
 *
 * @param {object} props
 * @param {string} props.legend               accessible group name
 * @param {string} [props.description]        optional descriptive text, linked via aria-describedby
 * @param {boolean} [props.visibleLegend]     render the legend visibly instead of sr-only
 * @param {string} [props.className]          classes for the options container
 * @param {React.ReactNode} props.children
 */
export default function RadioCardGroup({
  legend,
  description,
  visibleLegend = false,
  className = 'grid grid-cols-1 sm:grid-cols-2 gap-4',
  children,
}) {
  const descId = useId();

  return (
    // border-0 p-0 m-0: fieldset carries default browser chrome that would break the layout.
    <fieldset
      className="border-0 p-0 m-0"
      aria-describedby={description ? descId : undefined}
    >
      <legend
        className={
          visibleLegend
            ? 'text-lg font-semibold text-gray-900 leading-snug mb-2'
            : 'sr-only'
        }
      >
        {legend}
      </legend>

      {description && (
        <p id={descId} className="text-sm text-gray-600 leading-relaxed max-w-3xl mb-3">
          {description}
        </p>
      )}

      <div className={className}>{children}</div>
    </fieldset>
  );
}
